import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_URL}/api` });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const auth = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  requestReset: (data) => api.post('/password-reset/request', data),
  confirmReset: (data) => api.post('/password-reset/confirm', data),
};

export const services = {
  list: () => api.get('/services'),
  enable: (data) => api.post('/services/enable', data),
  disable: (data) => api.post('/services/disable', data),
  changeTier: (data) => api.patch('/services/tier', data),
};

export const keys = {
  list: () => api.get('/keys'),
  create: (data) => api.post('/keys', data),
  revoke: (id) => api.delete(`/keys/${id}`),
};

export const usage = {
  get: () => api.get('/usage'),
  hourly: () => api.get('/usage/hourly'),
  summary: () => api.get('/usage/summary'),
};

export const billing = {
  current: () => api.get('/billing/current'),
};

export const wallet = {
  get: () => api.get('/wallet'),
  transactions: (params) => api.get('/wallet/transactions', { params }),
  topup: (data) => api.post('/wallet/topup', data),
  topupStatus: (id) => api.get(`/wallet/topup/status/${id}`),
  updateAutoTopup: (data) => api.put('/wallet/auto-topup', data),
  setupPayment: (data) => api.post('/wallet/auto-topup/setup-payment', data),
  confirmSetup: () => api.post('/wallet/auto-topup/confirm-setup', {}),
};

export const subscription = {
  current: () => api.get('/subscription/current'),
  history: (params) => api.get('/subscription/history', { params }),
  proceed: (data) => api.post('/subscription/proceed', data),
  renewNow: () => api.post('/subscription/renew-now'),
  addServices: (data) => api.post('/subscription/add-services', data),
  queueChanges: (data) => api.post('/subscription/queue-changes', data),
  cancelPending: () => api.delete('/subscription/pending-changes'),
  toggleAutoRenew: (data) => api.put('/subscription/auto-renew', data),
};

export const llm = {
  fetchModels: (provider) => api.get(`/admin/llm/models/${provider}`),
};

export const settings = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

export const admin = {
  metrics: () => api.get('/admin/metrics'),
  config: () => api.get('/admin/config'),
  updateConfig: (data) => api.put('/admin/config', data),
  bulkUpdateConfig: (data) => api.put('/admin/config/bulk', data),
  getTierLimits: () => api.get('/admin/tier-limits'),
  updateTierLimit: (tier, data) => api.put(`/admin/tier-limits/${tier}`, data),
  users: (params) => api.get('/admin/users', { params }),
  userDetail: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  billing: () => api.get('/admin/billing'),
  logs: (params) => api.get('/admin/logs', { params }),
  gpuHealth: () => api.get('/admin/gpu-health'),
  getLanding: () => api.get('/admin/landing'),
  updateLanding: (data) => api.put('/admin/landing', data),
  deleteUserData: (id) => api.delete(`/admin/users/${id}/data`),
  deleteUser: (id, data) => api.request({ method: 'DELETE', url: `/admin/users/${id}`, data }),
  getUserWallet: (id) => api.get(`/admin/users/${id}/wallet`),
  creditWallet: (id, data) => api.post(`/admin/users/${id}/wallet/credit`, data),
  debitWallet: (id, data) => api.post(`/admin/users/${id}/wallet/debit`, data),
  analyticsOverview: (params) => api.get('/admin/analytics/overview', { params }),
  analyticsSections: (params) => api.get('/admin/analytics/sections', { params }),
  reportUser: (id, params) => api.get(`/admin/reports/user/${id}`, { params }),
  reportPlatform: (params) => api.get('/admin/reports/platform', { params }),
  exportSettings: () => api.get('/admin/export'),
  importSettings: (data) => api.post('/admin/import', data),
  getCustomerAssignments: (params) => api.get('/admin/customer-assignments', { params }),
  updateCustomerAssignment: (id, data) => api.put(`/admin/customer-assignments/${id}`, data),
  getAssignmentDistribution: () => api.get('/admin/customer-assignments/distribution'),
};

export const analytics = {
  pageview: (data) => api.post('/analytics/pageview', data).catch(() => {}),
  section: (data) => api.post('/analytics/section', data).catch(() => {}),
};

export const landing = {
  get: () => api.get('/landing'),
  pricing: () => api.get('/landing/pricing'),
  feature: (id) => api.get(`/features/${id}`),
};

export default api;
